/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * runDetailApi — the run behind one result: its route, and its splits.
 *
 * WHY THESE ARE SAVED QUERIES AND NOT TABLE READS. `event_result` is public,
 * because the official figures are COPIED onto it when a run is attached.
 * Everything else about the run — the GPS track, the title, the climb, the
 * calories — lives on `activity`, which is PRIVATE: an athlete's ordinary read
 * returns only their own. A board visitor has no session at all, so no table
 * read can reach it. A saved query can, because the engine runs developer SQL
 * rather than a caller's filter.
 *
 * THE GUARD IS THE SQL, AND ONLY THE SQL. Both queries are scope 'none' and
 * visibility 'public', which they have to be — the platform refuses a scoped
 * query that is public, and `{{caller_filter}}` is banned outright because an
 * anonymous caller has no id to bind. The consequence, read in
 * `validate_saved_query`: the load-bearing checks run only for SCOPED queries,
 * so none of them apply here. What protects these two is the two-hop in their
 * own WHERE clause — "the activity attached to THIS result, where that result
 * belongs to THIS race" — and nothing else.
 *
 * That is why this module never sends an activity id, and must never be
 * changed to. Both ids below come from the URL, which is a board anyone may
 * already read; pairing a real result with a race it does not belong to
 * returns nothing. Probed 2026-11-03, signed out: a mismatched pair, an
 * unknown result, an unknown race and a smuggled activity_id all returned no
 * rows or were refused.
 *
 * ONE ROW FOR THE WHOLE ROUTE. `analytics_runner` wraps every query in
 * LIMIT 501 (ROW_CAP = 500) and a 10K is well over a thousand GPS points, so
 * the track arrives as ONE aggregated string rather than a row per point. A
 * row per point would not have failed — it would have drawn a route that
 * stops a third of the way round, which is worse.
 */

import envConfig from '../../../config/envConfig';
import { recordsOf } from './boardApi';

export const RUN_DETAIL_QUERY = 'event_result_run_detail';
export const RUN_SPLITS_QUERY = 'event_result_run_splits';
/**
 * The same two for a run attached DAY BY DAY on a multi-day event: that run is
 * an event_result_split row, not the result, so it is reached through the
 * split's id as well. Still never an activity id — the server reads it from
 * the split's own label, and all three ids have to agree.
 */
export const DAY_RUN_DETAIL_QUERY = 'event_result_day_run_detail';
export const DAY_RUN_SPLITS_QUERY = 'event_result_day_run_splits';

/** A split id from the URL, or null. Only a positive whole number is one. */
export function splitIdOf(raw) {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

async function runPublicQuery(api, name, params, signal) {
  const query = new URLSearchParams(params).toString();
  const resp = await api.publicApiCall(
    `/arcee/public/${envConfig.organizationId}/${envConfig.applicationId}/analytics/${name}/run/?${query}`,
    { method: 'GET', signal },
  );
  if (!resp.ok) throw new Error('Could not load this run.');
  return recordsOf(await resp.json());
}

/**
 * The run behind one result, or null.
 *
 * The query always answers with a row when the two ids agree, even for an
 * activity that recorded nothing — `point_count` 0 and `track` null. That is
 * deliberate: it lets the page say "no route was recorded" rather than leaving
 * the reader to guess whether the read failed.
 */
export async function fetchRunDetail(api, {
  eventResultId, eventActivityId, splitId = null, signal,
} = {}) {
  if (!eventResultId || !eventActivityId) return null;
  const rows = splitId
    ? await runPublicQuery(api, DAY_RUN_DETAIL_QUERY, {
      event_result_id: eventResultId, event_activity_id: eventActivityId, split_id: splitId,
    }, signal)
    : await runPublicQuery(api, RUN_DETAIL_QUERY, {
      event_result_id: eventResultId, event_activity_id: eventActivityId,
    }, signal);
  return rows[0] || null;
}

/** The kilometre splits behind one result. An empty list is a normal answer. */
export async function fetchRunSplits(api, {
  eventResultId, eventActivityId, splitId = null, signal,
} = {}) {
  if (!eventResultId || !eventActivityId) return [];
  if (splitId) {
    return runPublicQuery(api, DAY_RUN_SPLITS_QUERY, {
      event_result_id: eventResultId, event_activity_id: eventActivityId, split_id: splitId,
    }, signal);
  }
  return runPublicQuery(api, RUN_SPLITS_QUERY, {
    event_result_id: eventResultId, event_activity_id: eventActivityId,
  }, signal);
}

// ── The track ───────────────────────────────────────────────────────────────

/**
 * "lat lng alt,lat lng alt,…" as points.
 *
 * Built by string_agg in the saved query, ordered by the recorded sequence.
 * Altitude is optional — the phone does not always have one — so a blank third
 * field parses to null rather than 0, which would draw a cliff.
 */
export function parseTrack(track) {
  if (!track) return [];
  const points = [];
  String(track).split(',').forEach((chunk) => {
    const bits = chunk.trim().split(/\s+/);
    const lat = Number(bits[0]);
    const lng = Number(bits[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const alt = bits[2] === undefined || bits[2] === '' ? null : Number(bits[2]);
    points.push({ lat, lng, alt: Number.isFinite(alt) ? alt : null });
  });
  return points;
}

/**
 * The track in the shape the app's own RouteMap and ElevationChart take:
 * `{ lat, lng, alt, distM }`, where distM is how far into the run that point
 * is.
 *
 * Those two components already draw this exact data in the athlete's own feed,
 * over OpenStreetMap tiles. Reusing them rather than redrawing means a run
 * looks the same on the public board as it does to the person who ran it —
 * and `elevationProfile` does the smoothing and downsampling here that it
 * already does there, instead of this page inventing a second way.
 */
export function routeWithDistance(points) {
  if (!points || points.length < 2) return [];
  const out = [{ ...points[0], distM: 0 }];
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += metresBetween(points[i - 1], points[i]);
    out.push({ ...points[i], distM: total });
  }
  return out;
}

const EARTH_METRES_PER_DEGREE = 111320;

/** Metres between two points, near enough at the scale of one race. */
function metresBetween(a, b) {
  const midLat = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  const dy = (b.lat - a.lat) * EARTH_METRES_PER_DEGREE;
  const dx = (b.lng - a.lng) * EARTH_METRES_PER_DEGREE * Math.cos(midLat);
  return Math.hypot(dx, dy);
}

/**
 * The track as an SVG path, sized to `width` × `height`.
 *
 * Longitude is squeezed by cos(latitude) before fitting, so the route keeps
 * its real proportions: without it a north–south loop in Hyderabad draws about
 * 5% too wide, which is enough to turn a circuit into an egg. One scale is used
 * for both axes for the same reason — fitting each independently would stretch
 * the shape to the box.
 *
 * `every` thins the drawn path. A marathon arrives as thousands of points and
 * a phone does not need all of them to draw a line a few hundred pixels wide;
 * the figures on the page always come from the full track, never from this.
 */
export function projectTrack(points, { width, height, pad = 14, every = 1 } = {}) {
  if (!points || points.length < 2) return null;
  const cosLat = Math.cos((points[0].lat * Math.PI) / 180) || 1;

  let minX = Infinity; let maxX = -Infinity; let minY = Infinity; let maxY = -Infinity;
  points.forEach((p) => {
    const x = p.lng * cosLat;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (p.lat < minY) minY = p.lat;
    if (p.lat > maxY) maxY = p.lat;
  });

  const spanX = maxX - minX || 1e-9;
  const spanY = maxY - minY || 1e-9;
  // One scale for both axes keeps the shape honest; the smaller of the two fits.
  const scale = Math.min((width - pad * 2) / spanX, (height - pad * 2) / spanY);
  const offX = (width - spanX * scale) / 2;
  const offY = (height - spanY * scale) / 2;

  const at = (p) => ({
    x: offX + (p.lng * cosLat - minX) * scale,
    // SVG y grows downwards; north must be up.
    y: height - (offY + (p.lat - minY) * scale),
  });

  const step = Math.max(1, Math.floor(every));
  const drawn = [];
  for (let i = 0; i < points.length; i += step) drawn.push(at(points[i]));
  const last = at(points[points.length - 1]);
  if (drawn.length === 0 || drawn[drawn.length - 1].x !== last.x) drawn.push(last);

  const d = drawn
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');

  return { d, start: at(points[0]), end: last, at, drawn: drawn.length };
}

/**
 * How tall the route is against how wide, on the ground.
 *
 * The map box is sized from this rather than fixed. A fixed wide box left a
 * round city loop sitting in a third of its width with empty ground either
 * side — the route is the content, so the box should take the route's shape
 * instead of the route being padded out to the box's.
 */
export function trackAspect(points) {
  if (!points || points.length < 2) return 1;
  const cosLat = Math.cos((points[0].lat * Math.PI) / 180) || 1;
  let minX = Infinity; let maxX = -Infinity; let minY = Infinity; let maxY = -Infinity;
  points.forEach((p) => {
    const x = p.lng * cosLat;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (p.lat < minY) minY = p.lat;
    if (p.lat > maxY) maxY = p.lat;
  });
  const spanX = maxX - minX;
  const spanY = maxY - minY;
  if (!(spanX > 0) || !(spanY > 0)) return 1;
  return spanY / spanX;
}

/**
 * Markers along the route, one every `everyMetres`.
 *
 * Measured from the real points rather than spaced by index: a phone records
 * more points when it is moving slowly, so index-spacing bunches the markers
 * on the hills.
 */
export function routeMarkers(points, projection, { everyMetres = 1000, max = 14 } = {}) {
  if (!points || points.length < 2 || !projection) return [];

  let total = 0;
  for (let i = 1; i < points.length; i += 1) total += metresBetween(points[i - 1], points[i]);

  // Never more than `max` marks: 42 of them on a marathon is a dotted line.
  let spacing = everyMetres;
  while (total / spacing > max) spacing += everyMetres;

  const marks = [];
  let travelled = 0;
  let nextAt = spacing;
  for (let i = 1; i < points.length; i += 1) {
    travelled += metresBetween(points[i - 1], points[i]);
    while (travelled >= nextAt && marks.length < max) {
      const here = projection.at(points[i]);
      marks.push({ label: String(Math.round(nextAt / 1000)), x: here.x, y: here.y });
      nextAt += spacing;
    }
  }
  return { marks, totalMetres: total, spacingMetres: spacing };
}

/** The altitude series, thinned to `count` samples for a small chart. */
export function elevationSeries(points, count = 120) {
  const withAlt = (points || []).filter((p) => p.alt !== null);
  if (withAlt.length < 2) return null;
  const step = Math.max(1, Math.floor(withAlt.length / count));
  const sampled = [];
  for (let i = 0; i < withAlt.length; i += step) sampled.push(withAlt[i].alt);
  sampled.push(withAlt[withAlt.length - 1].alt);
  const lo = Math.min(...sampled);
  const hi = Math.max(...sampled);
  return { sampled, lo, hi };
}
